import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";
import { sponsorsQueryOptions, type Sponsor } from "@/lib/sponsors.queries";
import { publicSettingsQueryOptions } from "@/lib/settings.queries";
import { mailtoLink } from "@/lib/settings.functions";
import { Input } from "@/components/ui/input";
import { PageHero } from "@/components/site/PageHero";

export const Route = createFileRoute("/sponsors")({
  head: () => ({
    meta: [
      { title: "AIA Sponsors — Medical Insight" },
      {
        name: "description",
        content:
          "The companies that support the Aesthetic Industry Association and the medical aesthetic community.",
      },
      { property: "og:title", content: "AIA Sponsors — Medical Insight" },
      {
        property: "og:description",
        content:
          "The companies that support the Aesthetic Industry Association and the medical aesthetic community.",
      },
    ],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(sponsorsQueryOptions());
  },
  component: SponsorsPage,
});

const TIER_ORDER: Sponsor["tier"][] = ["Platinum", "Gold", "Silver", "Partner"];

const TIER_META: Record<
  Sponsor["tier"],
  { label: string; description: string }
> = {
  Platinum: {
    label: "Platinum Sponsors",
    description: "Founding-level partners driving the industry forward.",
  },
  Gold: {
    label: "Gold Sponsors",
    description: "Long-standing supporters of the AIA mission.",
  },
  Silver: {
    label: "Silver Sponsors",
    description: "Trusted partners across the aesthetic ecosystem.",
  },
  Partner: {
    label: "AIA Sponsors",
    description: "The companies that make our work possible.",
  },
};

function SponsorsPage() {
  return (
    <Suspense
      fallback={
        <div className="mx-auto max-w-7xl px-4 py-16 text-sm text-muted-foreground">
          Loading sponsors…
        </div>
      }
    >
      <SponsorsContent />
    </Suspense>
  );
}

function SponsorsContent() {
  const { data: all } = useSuspenseQuery(sponsorsQueryOptions());
  // Settings > Organization > "Sponsorship email"; defaults to the address the page shipped with.
  const { data: settings } = useQuery(publicSettingsQueryOptions());
  const [q, setQ] = useState("");

  const active = useMemo(
    () =>
      [...all]
        .filter((s) => s.is_active)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [all],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return active;
    return active.filter((s) => s.name.toLowerCase().includes(needle));
  }, [active, q]);

  const grouped = useMemo(() => {
    const out: Record<Sponsor["tier"], Sponsor[]> = {
      Platinum: [],
      Gold: [],
      Silver: [],
      Partner: [],
    };
    filtered.forEach((s) => out[s.tier].push(s));
    return out;
  }, [filtered]);

  return (
    <div className="bg-background">
      <PageHero
        page="sponsors"
        kicker="Aesthetic Industry Association"
        title="AIA Sponsors"
        subtitle={
          <>
            <span className="block text-sm text-white/75">
              Active sponsors
            </span>
          </>
        }
        aside={
          <Input
            placeholder="Search sponsors…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-white/95"
          />
        }
      />

      {/* Tier sections */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:py-16">
        {TIER_ORDER.map((tier) => {
          const list = grouped[tier];
          if (list.length === 0) return null;
          const meta = TIER_META[tier];
          return (
            <section key={tier} className="mb-14 last:mb-0">
              <div className="mb-6 flex items-end justify-between gap-4 border-b pb-3">
                <div>
                  <h2 className="text-xl font-bold tracking-tight sm:text-2xl">
                    {meta.label}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {meta.description}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5">
                {list.map((s) => (
                  <SponsorTile key={s.id} sponsor={s} />
                ))}
              </div>
            </section>
          );
        })}

        {filtered.length === 0 && (
          <div className="py-16 text-center text-sm text-muted-foreground">
            No sponsors match your search.
          </div>
        )}
      </div>

      {/* CTA */}
      <section className="border-t bg-secondary/30">
        <div className="mx-auto max-w-4xl px-4 py-12 text-center">
          <h3 className="text-xl font-semibold tracking-tight sm:text-2xl">
            Interested in sponsoring the AIA?
          </h3>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground">
            Reach decision-makers and clinicians across the medical aesthetic
            industry through AIA programs, research, and events.
          </p>
          <a
            href={mailtoLink(settings!.org.sponsorshipEmail, "AIA Sponsorship Inquiry")}
            className="mt-5 inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
          >
            Become a sponsor
          </a>
        </div>
      </section>
    </div>
  );
}

function SponsorTile({ sponsor }: { sponsor: Sponsor }) {
  const inner = (
    <div className="group relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg border bg-card px-3 py-2 transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md sm:px-4 sm:py-3">
      {sponsor.logo_url ? (
        <img
          src={sponsor.logo_url}
          alt={sponsor.name}
          title={sponsor.name}
          loading="lazy"
          className="h-full w-full scale-110 object-contain transition-transform duration-300 group-hover:scale-[1.18]"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center rounded-md bg-secondary/60 px-2 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {sponsor.name}
        </div>
      )}
    </div>
  );

  if (sponsor.website_url) {
    return (
      <a
        href={sponsor.website_url}
        target="_blank"
        rel="noreferrer"
        aria-label={sponsor.name}
        title={sponsor.name}
        className="block h-full"
      >
        {inner}
      </a>
    );
  }
  return inner;
}
